"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Reveal } from "@/components/ui/Reveal";
import { submitForm } from "@/lib/submitForm";

export type FaqItem = {
  question: string;
  answer: string;
};

const defaultFaqs: FaqItem[] = [
  {
    question:
      "What is the difference between self-publishing and hybrid publishing?",
    answer:
      "Self-publishing means you handle everything or hire individual freelancers. Hybrid publishing offers a team approach with shared investment. We guide you through both options. Some authors want full control. Others prefer more support. We explain the pros and cons honestly so you can choose what fits your goals and budget.",
  },
  {
    question: "How long does the publishing process take?",
    answer:
      "Most books take four to six months from manuscript to market. That covers editing, design, formatting, and distribution setup. Some projects move faster if the manuscript is already polished. Others take longer for complex design or extensive revisions. We give you a clear timeline upfront. No guessing. No waiting in the dark.",
  },
  {
    question: "Do I retain the rights to my book?",
    answer:
      "Yes. You keep full rights to your work. We never ask for ownership or royalties. Unlike traditional publishers who take control, you decide where to sell, how to price, and what comes next. Your book belongs to you. We are simply here to help you bring it into the world with professionalism and care.",
  },
  {
    question: "Will my book be available in bookstores?",
    answer:
      "Yes. We offer distribution that reaches online retailers and physical bookstores. Amazon, Barnes & Noble, and independent shops can all stock your book. Expanded distribution options make it possible for stores to order copies. We walk you through the setup so you understand exactly where your book will appear.",
  },
  {
    question: "Can you help me if I have already self-published?",
    answer:
      "Absolutely. Many authors come to us after publishing on their own. They want better covers, cleaner interiors, or help with marketing. We offer services for books already released. You can upgrade your cover, fix formatting issues, or get professional editing for a second edition. It is never too late to improve your book.",
  },
  {
    question: "How much does it cost to publish with you?",
    answer:
      "Costs vary based on your book length, genre, and the services you choose. We offer transparent pricing with no hidden fees. A basic package includes editing, cover design, formatting, and distribution. Add-ons like marketing or illustration increase the investment. We provide a detailed quote after learning about your project so there are no surprises.",
  },
];

const serviceOptions = [
  "Self Publishing",
  "Book Editing",
  "Book Cover Design",
  "Book Printing",
  "Book Marketing",
  "PR & Publicity",
  "Book Illustration",
  "Ghostwriting",
  "Book Formatting",
];

const selectClass =
  "w-full appearance-none rounded-md border-0 bg-[#49622d] px-4 py-2.5 text-sm text-white outline-none transition focus:ring-2 focus:ring-[#b46c29] lg:py-2";

const inputClass =
  "w-full rounded-md border-0 bg-[#49622d] px-4 py-2.5 text-sm text-white placeholder:text-white/70 outline-none transition focus:ring-2 focus:ring-[#b46c29] lg:py-2";

export type FaqSectionProps = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  faqs?: FaqItem[];
  className?: string;
};

export function FaqSection({
  eyebrow = "Frequently Asked Questions",
  heading = "Common Questions About Book Publishing",
  description = "A book is a journey. Book publishing should feel like a partnership, not a maze. Let us walk beside you from manuscript to market with honesty and care.",
  faqs = defaultFaqs,
  className,
}: FaqSectionProps) {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState(0);
  const [services, setServices] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");

  function toggleService(service: string) {
    setServices((prev) =>
      prev.includes(service)
        ? prev.filter((item) => item !== service)
        : [...prev, service],
    );
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");

    try {
      const form = event.currentTarget;
      const data = new FormData(form);

      const fields = {
        manuscriptReady: String(data.get("manuscriptReady") ?? ""),
        publishedBefore: String(data.get("publishedBefore") ?? ""),
        genre: String(data.get("genre") ?? ""),
        services: services.join(", "),
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        phone: String(data.get("phone") ?? ""),
        message: String(data.get("message") ?? ""),
      };

      const result = await submitForm({
        source: "faq-lead-form",
        fields,
        services,
        honeypot,
      });

      if (!result.ok) {
        setStatus("error");
        setStatusMessage(result.error);
        return;
      }

      if (typeof window !== "undefined" && Array.isArray((window as unknown as { dataLayer?: unknown[] }).dataLayer)) {
        (window as unknown as { dataLayer: unknown[] }).dataLayer.push({
          event: "faq_lead_form_submit",
          ...fields,
        });
      }

      form.reset();
      setServices([]);
      router.push("/thank-you/");
    } catch (error) {
      console.error("FAQ lead form submission error:", error);
      setStatus("error");
      setStatusMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section
      id="faq"
      className={["bg-white py-12 lg:py-10", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center lg:mb-6">
          <Reveal>
            <p className="font-heading text-2xl font-semibold text-[#29361a] sm:text-[30px] lg:text-[35px]">
              {eyebrow}
            </p>
          </Reveal>
          <Reveal delayMs={120}>
            <h2 className="mt-1 font-heading text-4xl font-bold text-[#b46c29] sm:text-5xl lg:text-[50px] lg:leading-[56px]">
              {heading}
            </h2>
          </Reveal>
          <Reveal delayMs={220}>
            <p className="mt-4 text-base leading-relaxed text-gray-600 lg:mt-2 lg:text-sm">
              {description}
            </p>
          </Reveal>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-10">
          <Reveal variant="left">
            <div className="relative overflow-hidden rounded-[20px] bg-[#29361a] p-6 sm:p-8 lg:p-5">
              <div
                className="pointer-events-none absolute -bottom-8 -right-8 h-40 w-40 opacity-20"
                aria-hidden="true"
              >
                <svg viewBox="0 0 120 120" className="h-full w-full fill-[#1f2914]">
                  <path d="M90 10c-20 12-40 34-46 58-4 14 4 24 14 26 14 2 24-8 30-22 10-24 14-48 2-62z" />
                </svg>
              </div>

              <h3 className="relative font-heading text-2xl font-semibold tracking-wide text-white sm:text-[28px] lg:text-xl">
                Is Your Manuscript The Next Bestseller?
              </h3>

              <form onSubmit={handleSubmit} className="relative mt-6 space-y-4 lg:mt-3 lg:space-y-2.5">
                {/* Honeypot — hidden from real users, bots that autofill every field will trip it */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
                />

                <label
                  htmlFor="manuscriptReady"
                  className="block text-sm font-medium text-white lg:text-xs"
                >
                  Do you have a manuscript ready?
                </label>
                <select
                  id="manuscriptReady"
                  name="manuscriptReady"
                  required
                  defaultValue=""
                  aria-label="Do you have a manuscript ready?"
                  className={selectClass}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>

                <label
                  htmlFor="publishedBefore"
                  className="block text-sm font-medium text-white lg:text-xs"
                >
                  Have you published before?
                </label>
                <select
                  id="publishedBefore"
                  name="publishedBefore"
                  required
                  defaultValue=""
                  aria-label="Have you published before?"
                  className={selectClass}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>

                <label
                  htmlFor="genre"
                  className="block text-sm font-medium text-white lg:text-xs"
                >
                  What genre of book do you plan on publishing?
                </label>
                <select
                  id="genre"
                  name="genre"
                  required
                  defaultValue=""
                  aria-label="What genre of book do you plan on publishing?"
                  className={selectClass}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  <option value="fiction">Fiction</option>
                  <option value="non-fiction">Non-fiction</option>
                  <option value="informative">Informative</option>
                  <option value="biography">Biography</option>
                  <option value="autobiography">Autobiography</option>
                  <option value="memoir">Memoir</option>
                  <option value="childrens">Children&apos;s Book</option>
                </select>

                <div>
                  <p className="mb-2 text-sm font-medium text-white lg:mb-1.5 lg:text-xs">
                    What services are you looking for?
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:gap-1.5">
                    {serviceOptions.map((service) => {
                      const checked = services.includes(service);
                      return (
                        <label
                          key={service}
                          className="flex cursor-pointer items-center gap-2 text-sm text-white lg:text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleService(service)}
                            className="peer sr-only"
                          />
                          <span
                            className={[
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-[#b46c29]",
                              checked ? "bg-[#b46c29]" : "bg-transparent",
                            ].join(" ")}
                            aria-hidden="true"
                          >
                            {checked && (
                              <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            )}
                          </span>
                          <span>{service}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Your name here"
                  className={inputClass}
                />

                <div className="grid gap-4 sm:grid-cols-2 lg:gap-2.5">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="info@example.com"
                    className={inputClass}
                  />
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(999) 999 9999"
                    className={inputClass}
                  />
                </div>

                <textarea
                  name="message"
                  rows={3}
                  placeholder="Your Message"
                  className={`${inputClass} resize-none`}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-md bg-[#b46c29] px-6 py-3 font-heading text-sm font-semibold uppercase tracking-wider text-white transition hover:bg-[#734214] disabled:cursor-not-allowed disabled:opacity-60 lg:py-2.5"
                >
                  {isSubmitting ? "Submitting..." : "Submit Now"}
                </button>

                {status !== "idle" && (
                  <p
                    role="status"
                    className={
                      status === "success"
                        ? "text-sm text-white"
                        : "text-sm text-red-300"
                    }
                  >
                    {statusMessage}
                  </p>
                )}
              </form>
            </div>
          </Reveal>

          <Reveal variant="right" delayMs={120}>
            <div className="space-y-3" role="list">
              {faqs.map((faq, index) => {
                const isOpen = openIndex === index;

                return (
                  <div key={faq.question} role="listitem">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpenIndex((prev) => (prev === index ? -1 : index))
                      }
                      className={[
                        "flex w-full items-center justify-between gap-3 rounded-md px-4 py-3 text-left transition-colors duration-300",
                        isOpen
                          ? "bg-[#b46c29] text-white"
                          : "border border-[#b46c29]/50 bg-[#fdf6ef] text-gray-800 hover:border-[#b46c29]",
                      ].join(" ")}
                    >
                      <span className="font-heading text-sm font-semibold sm:text-base">
                        {faq.question}
                      </span>
                      {isOpen ? (
                        <Minus className="h-4 w-4 shrink-0" aria-hidden="true" />
                      ) : (
                        <Plus
                          className="h-4 w-4 shrink-0 text-[#b46c29]"
                          aria-hidden="true"
                        />
                      )}
                    </button>

                    <div
                        className={[
                        "grid transition-all duration-300 ease-out",
                        isOpen
                          ? "mt-0 grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0",
                      ].join(" ")}
                    >
                      <div className="overflow-hidden">
                        <div className="rounded-b-md bg-[#f8efe4] px-4 py-4 text-sm leading-relaxed text-gray-700 sm:text-base">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
